/**
 * CredentialCapabilityService.ts
 *
 * Redux thunks for credential capability re-probe flows. Two surfaces:
 *
 * - `recheckDomainAvailabilityThunk` (Slice 2.3 FE) — per-domain re-probe
 *   fired from `DomainPickerBlock`'s unavailable-card info modal "Re-check
 *   availability" button. On success, updates the domain's `availability`
 *   field in `dashboardDomainRegistrySlice` so the card transitions from
 *   unavailable → available without a page reload (§37b).
 *
 * - `recheckCredentialAvailability` (Slice 2.4 FE) — credential-level
 *   re-probe fired from the credential picker's blocked-row "Re-check now"
 *   button. On success, patches the Redux `availableCredentials` entry
 *   in-place via `dashboardCreationActions.setAvailabilityForCredential`
 *   so the picker row re-renders from blocked → selectable without a full
 *   credentials list refetch.
 *
 * Both call the same BE endpoint
 * (`POST /v1/credentials/:credentialId/capability/probe`); the per-domain
 * variant passes `{ domainId }` in the body, the credential-level variant
 * passes no body. Both error paths return a string for inline display (§37b:
 * no toasts behind modal overlay).
 *
 * Dashboard-era only.
 */

import type { Action } from '@reduxjs/toolkit'
import type { ThunkAction } from 'redux-thunk'
import { CredentialCapabilityAPI } from '@/API/CredentialCapability'
import type { CredentialProbeRawMap } from '@/API/CredentialCapability'
import type { AppDispatch, RootState } from '@/redux'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import { dashboardDomainRegistryActions } from '@/redux/slice/dashboardDomainRegistrySlice'
import type {
  CredentialAvailabilitySummary,
  CredentialCapabilityAvailability,
} from '@/types/DashboardV1.types'

// ─── Slice 2.3 FE — per-domain re-probe ───────────────────────────────────────

/**
 * recheckDomainAvailabilityThunk — user-driven manual re-probe for a single
 * (credentialId, domainId / pickerKey) pair.
 *
 * Dispatched from:
 *   - DomainPickerBlock's unavailable-card info modal "Re-check availability"
 *     button (Slice 2.3).
 *
 * On success: updates the domain's `availability` field in the
 * `dashboardDomainRegistrySlice` in place — the card transitions from
 * unavailable to available without a page reload.
 *
 * On failure / timeout: returns the error string for the caller to surface
 * inline (per §37b — NOT a toast, which renders behind the modal overlay).
 *
 * Dashboard-era only. `credentialId` must be the UUID of the credential
 * associated with this domain's appCode at Step 2 of the creation flow.
 *
 * @param token             - JWT bearer token for the authenticated user.
 * @param credentialId      - UUID of the credential to probe.
 * @param pickerKey         - Era-agnostic picker key (e.g. 'staff').
 * @param qualifiedDomainId - Dashboard-era qualified id (e.g.
 *   'campminder.staff-dashboard'). Sent to the BE as `domainId`.
 *
 * @returns A thunk that resolves to `'available'` on probe success,
 *   `'available'` when the probe returns `'unknown'` (fail-open — the card
 *   transitions to selectable and the modal closes; the modal must NOT show
 *   "Still unavailable" when the card is already selectable),
 *   `'unavailable'` when the probe re-confirms unavailability, or an error
 *   string on network / timeout failure.
 */
export const recheckDomainAvailabilityThunk =
  (
    token: string,
    credentialId: string,
    pickerKey: string,
    qualifiedDomainId: string,
  ): ThunkAction<
    Promise<'available' | 'unavailable' | string>,
    RootState,
    unknown,
    Action
  > =>
  async dispatch => {
    const result = await CredentialCapabilityAPI.probeDomain(
      token,
      credentialId,
      qualifiedDomainId,
    )

    if (result.err !== undefined || result.data === undefined) {
      // Network / timeout / auth failure — return the error message so the
      // caller can surface it inline inside the modal (§37b).
      return result.err ?? 'Re-check failed. Check your connection and try again.'
    }

    const availability = result.data
    // Update the registry slice in place so the card transitions without a
    // page reload (§37b "On success (probe returns available)").
    dispatch(
      dashboardDomainRegistryActions.updateDomainAvailability({
        pickerKey,
        availability,
      }),
    )

    // Treat 'unknown' the same as 'available' for the modal close path. The
    // picker fail-opens for 'unknown' (card becomes selectable), so returning
    // 'unavailable' here would produce contradictory UX: card is selectable
    // but modal says "Still unavailable." See Claude finding #1 / Qodo #3
    // (Slice 2.3 FE Round 1 PR Review Pass).
    return availability.state === 'unavailable' ? 'unavailable' : 'available'
  }

// ─── Slice 2.4 FE — credential-level re-probe ─────────────────────────────────

/**
 * deriveAvailabilitySummaryFromRawMap — bridges the probe endpoint's raw
 * per-domain map to the `CredentialAvailabilitySummary` shape consumed by
 * `setAvailabilityForCredential` and `CredentialPickerExistingBlock`.
 *
 * The BE all-domains probe returns `Record<domainId, CredentialCapabilityAvailability>`
 * — a raw map with no `overall`/`blockedReason` wrapper. This function derives
 * those aggregate fields using the same priority rules as the BE's
 * `deriveCredentialStatusState` (TDD §"Canonical 6-State Mapping"):
 *
 * Overall rules (ordered; first match wins):
 * 1. Exclude `not_implemented` stub domains from consideration.
 * 2. Any considered domain `state === 'available'`               → overall 'available'
 * 3. Any `unavailable` + `auth_failed` or `access_denied`        → overall 'unavailable'
 *    (blockedReason is set to the first such entry's reason + userMessage)
 * 4. Mix of available + unavailable                              → 'mixed'
 * 5. All remaining (all unknown / transient / empty map)         → 'unknown'
 *
 * `lastChecked` is the latest ISO-8601 `lastChecked` across all entries (or
 * the current time if no entries carry timestamps).
 *
 * This function is ONLY used on the re-probe path. The normal read path gets
 * `availabilitySummary` pre-computed by the BE on `GET /v1/credentials`.
 *
 * Dashboard-era only.
 */
export function deriveAvailabilitySummaryFromRawMap(
  credentialId: string,
  rawMap: CredentialProbeRawMap,
): { credentialId: string; availabilitySummary: CredentialAvailabilitySummary } {
  const entries = Object.entries(rawMap)

  // Exclude not_implemented stubs so they don't drag down a healthy credential.
  const considered = entries.filter(
    ([, entry]) =>
      !(entry.state === 'unknown' && entry.reason === 'not_implemented'),
  )

  // Determine the latest lastChecked across all entries (fall back to now).
  const allTimestamps = entries
    .map(([, e]) => e.lastChecked)
    .filter(Boolean)
  const lastChecked =
    allTimestamps.length > 0
      ? allTimestamps.reduce((a, b) => (a > b ? a : b))
      : new Date().toISOString()

  const perDomain = Object.fromEntries(entries) as Record<
    string,
    CredentialCapabilityAvailability
  >

  // Rule 1: any considered domain available → overall available.
  if (considered.some(([, e]) => e.state === 'available')) {
    return {
      credentialId,
      availabilitySummary: { overall: 'available', perDomain, lastChecked },
    }
  }

  // Rule 2: any unavailable + blocking reason → overall unavailable.
  const blockingEntry = considered.find(
    ([, e]) =>
      e.state === 'unavailable' &&
      (e.reason === 'auth_failed' || e.reason === 'access_denied'),
  )
  if (blockingEntry) {
    const [, entry] = blockingEntry
    return {
      credentialId,
      availabilitySummary: {
        overall: 'unavailable',
        perDomain,
        lastChecked,
        blockedReason: {
          reason: entry.reason as 'auth_failed' | 'access_denied',
          userMessage: entry.userMessage ?? '',
        },
      },
    }
  }

  // Rule 3: mix of available (skipped — no available if we're here) + other
  // unavailable entries (non-blocking reasons) → mixed.
  if (considered.some(([, e]) => e.state === 'unavailable')) {
    return {
      credentialId,
      availabilitySummary: { overall: 'mixed', perDomain, lastChecked },
    }
  }

  // Rule 4: all remaining (unknown / transient / empty map) → unknown.
  return {
    credentialId,
    availabilitySummary: { overall: 'unknown', perDomain, lastChecked },
  }
}

export interface RecheckCredentialResult {
  data?: {
    credentialId: string
    availabilitySummary: CredentialAvailabilitySummary
  }
  err?: string
}

/**
 * recheckCredentialAvailability — fires the credential-level probe endpoint
 * and, on success, derives `CredentialAvailabilitySummary` from the raw
 * per-domain map returned by the BE, then patches the Redux availableCredentials
 * entry in-place via `setAvailabilityForCredential`.
 *
 * Components show a loading spinner during the in-flight period and react to
 * the slice update; on success, the blocked credential row converts to a
 * selectable radiogroup pill without a full credentials list refetch.
 *
 * The BE `POST /v1/credentials/:id/capability/probe` (no body) returns a RAW
 * `Record<domainId, CredentialCapabilityAvailability>` map. This thunk bridges
 * that to the `CredentialAvailabilitySummary` shape via
 * `deriveAvailabilitySummaryFromRawMap`.
 *
 * @param token        - JWT bearer token for the authenticated user.
 * @param credentialId - UUID of the credential to probe.
 *
 * @returns A thunk that resolves to `{ data: { credentialId, availabilitySummary } }`
 *   on success, or `{ err: string }` on failure. Callers surface the error
 *   inline below the blocked-row Re-check button.
 */
export const recheckCredentialAvailability =
  (token: string, credentialId: string) =>
  async (dispatch: AppDispatch): Promise<RecheckCredentialResult> => {
    const resp = await CredentialCapabilityAPI.probeAllDomains(token, credentialId)
    if (resp.data !== undefined) {
      const derived = deriveAvailabilitySummaryFromRawMap(credentialId, resp.data)
      dispatch(
        dashboardCreationActions.setAvailabilityForCredential({
          credentialId: derived.credentialId,
          availabilitySummary: derived.availabilitySummary,
        }),
      )
      return { data: derived }
    }
    return { err: resp.err ?? 'Re-check failed' }
  }
